import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_LIST_LIMIT, deriveListPage, SortDirSchema } from '../lib/list';
import { type RouterOutputs, trpc } from '../lib/trpc';
import {
  USERS_SORT_DEFAULT,
  type UsersSort,
  type UsersSortBy,
  UsersSortBySchema,
} from '../lib/users-list';

export type WorkspaceUserListItem = RouterOutputs['users']['list']['items'][number];

export const USERS_PAGE_SIZE = DEFAULT_LIST_LIMIT;

/**
 * Per-field `safeParse` rather than one parse of the pair: a hand-edited
 * `?sortBy=nonsense` should cost you the field you broke, not the valid `?sortDir=`
 * sitting next to it.
 */
function sortFromParams(params: URLSearchParams): UsersSort {
  const by = UsersSortBySchema.safeParse(params.get('sortBy'));
  const dir = SortDirSchema.safeParse(params.get('sortDir'));
  return {
    by: by.success ? by.data : USERS_SORT_DEFAULT.by,
    dir: dir.success ? dir.data : USERS_SORT_DEFAULT.dir,
  };
}

/**
 * Drives the workspace Users directory: server-side sorting and offset pagination
 * over `users.list`, shared by both portals so the behavior — and the query-key
 * shape — stays identical rather than drifting per app.
 *
 * There is deliberately no search or filter state. A workspace holds tens of
 * members, not thousands of contacts, so the whole directory is a page or two;
 * adding a filter bar would be more UI than the data warrants. If that changes,
 * the endpoint is already on the standard envelope (`filter` is just empty today).
 *
 * URL-backed (`?sortBy=`/`?sortDir=`/`?cursor=`) so a sorted/paged view is
 * shareable by link, matching `useContactsSearch`.
 */
export function useUsersList() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Lazy initializer — runs exactly once, at mount — seeds state from the URL.
  // State is the source of truth afterward and mirrors back out.
  const [initial] = React.useState(() => ({
    sort: sortFromParams(searchParams),
    cursor: Number(searchParams.get('cursor')) || 0,
  }));

  const [sort, setSortState] = React.useState<UsersSort>(initial.sort);
  const [cursor, setCursor] = React.useState(initial.cursor);

  // Read through a ref so this only mirrors when the *sort/cursor* change, not
  // every time the router hands back a fresh URLSearchParams identity — including
  // from this effect's own writes.
  const searchParamsRef = React.useRef(searchParams);
  searchParamsRef.current = searchParams;

  React.useEffect(() => {
    const nextParams = new URLSearchParams(searchParamsRef.current);
    const updates: Record<string, string | null> = {
      sortBy: sort.by !== USERS_SORT_DEFAULT.by ? sort.by : null,
      sortDir: sort.dir !== USERS_SORT_DEFAULT.dir ? sort.dir : null,
      cursor: cursor > 0 ? String(cursor) : null,
    };
    for (const [key, value] of Object.entries(updates)) {
      if (value === null) nextParams.delete(key);
      else nextParams.set(key, value);
    }
    setSearchParams(nextParams, { replace: true });
  }, [sort, cursor, setSearchParams]);

  const query = trpc.users.list.useQuery({ sort, cursor, limit: USERS_PAGE_SIZE });

  const items = query.data?.items ?? [];
  const totalCount = query.data?.totalCount ?? 0;

  /** Click a column: flip direction if it's already the sort key, otherwise adopt it. */
  const toggleSort = (by: UsersSortBy) => {
    setSortState((prev) =>
      prev.by === by
        ? { by, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { by, dir: USERS_SORT_DEFAULT.dir }
    );
    // The current page number means nothing under a new ordering.
    setCursor(0);
  };

  return {
    items,
    totalCount,
    isLoading: query.isLoading,
    isError: query.isError,
    sort,
    toggleSort,
    ...deriveListPage(cursor, items, totalCount, query.data?.nextCursor),
    goToNextPage: () => {
      if (query.data?.nextCursor != null) setCursor(query.data.nextCursor);
    },
    goToPrevPage: () => setCursor((c) => Math.max(0, c - USERS_PAGE_SIZE)),
    pageSize: USERS_PAGE_SIZE,
  };
}
